import { Animated, Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { EffortScale } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { EFFORT_TICK_WIDTH, effortRampStopOf, effortTickLabel } from "./effortWheel";

// ONE stop of the effort meter (LG-63) — the number, its ramp colour, the
// selection ring, the prescribed-effort ghost and the PR pulse. Extracted out
// of <EffortMeter> when the five fixed segments became an eleven-tick wheel
// and the file went over the 500-line cap.
//
// ⭐ It is deliberately the ONLY tick renderer: the scrolling wheel and the
// collapsed AMRAP band both render this component, so there is exactly one
// commit gesture to keep honest. `wide` swaps the width rule and nothing else.

export type EffortTickProps = {
  theme: UiTheme;
  /** The canonical rir this tick commits. Also its printed number, unless the
   *  athlete reads RPE or the caller overrides the label. */
  rir: number;
  /** Display scale. Labels only — the committed value never changes. */
  scale: EffortScale;
  /** Take the row's width instead of one tick's: the collapsed AMRAP band is
   *  a single stop, so there is no wheel for it to sit on. */
  wide?: boolean;
  /** Prints (and announces) this in place of the scale number — the AMRAP
   *  verb, "Done". The value committed is still `rir`. */
  labelOverride?: string;
  selected: boolean;
  /** Prescribed here, and not yet answered: draws the ghost. */
  isTarget: boolean;
  flashing: boolean;
  flashOpacity: Animated.Value;
  disabled: boolean;
  /** accessibilityHint from the host — what the tap DOES beyond picking an
   *  effort, which the visual tick can't say. */
  hint?: string;
  onSelect: (rir: number) => void;
};

export function EffortTick({
  theme,
  rir,
  scale,
  wide = false,
  labelOverride,
  selected,
  isTarget,
  flashing,
  flashOpacity,
  disabled,
  hint,
  onSelect,
}: EffortTickProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const stop = theme.effortRamp[effortRampStopOf(rir)];
  if (!stop) return null;

  const scaleName = scale === "rir" ? "RIR" : "RPE";
  const printed = effortTickLabel(rir, scale);

  return (
    <Pressable
      accessibilityRole="radio"
      accessibilityLabel={
        labelOverride ??
        (isTarget
          ? // Composed, not interpolated: the scale + number are assembled by
            // hand below and the localized part is one WORD. One less moving
            // piece, and the label reads identically in every locale.
            // ⭐ It is also the only way the target reaches someone who cannot
            // see the mark — and, since LG-63, the only way it reaches anyone
            // whose prescribed value has scrolled outside the window.
            `${scaleName} ${printed}, ${t("mobile.logger.effortMeterTargetWord", {
              defaultValue: "target",
            })}`
          : `${scaleName} ${printed}`)
      }
      accessibilityHint={hint}
      accessibilityState={{ selected, checked: selected, disabled }}
      // ⛔ Measured in the bench DOM (LG-63, 3/09): react-native-web drops
      // `accessibilityState` on the floor — 86 radios rendered, zero
      // `aria-checked` — so a web screen reader never heard which value was
      // chosen. `aria-checked` is a first-class RN prop since 0.73 and reaches
      // the DOM on RNW, so it is the same fact stated in the one dialect both
      // platforms speak. The state prop stays for the native readers that
      // already worked. [[reference_rnw_silently_drops_rn_a11y_props]]
      aria-checked={selected}
      disabled={disabled}
      onPress={() => onSelect(rir)}
      style={({ pressed }) => [
        wide ? styles.wide : styles.tick,
        { backgroundColor: stop.fill },
        selected && { borderWidth: 2, borderColor: stop.on },
        pressed && !disabled && styles.pressed,
        disabled && styles.disabled,
      ]}
    >
      {/* Takes the stop's OWN validated ink, the one the number uses: the ramp
          crosses the mid-lightness zone where a theme-global ink2 dies, and the
          AA gate is per-stop (effortRampContrast). */}
      {isTarget ? (
        <View
          pointerEvents="none"
          style={[styles.targetMark, { borderColor: stop.on }]}
        />
      ) : null}
      {flashing ? (
        <Animated.View
          pointerEvents="none"
          style={[styles.prFlash, { opacity: flashOpacity }]}
        />
      ) : null}
      <Text style={[styles.segmentNum, { color: stop.on }]} numberOfLines={1}>
        {labelOverride ?? printed}
      </Text>
    </Pressable>
  );
}

function makeStyles(theme: UiTheme) {
  const { radius: r, typography: t } = theme;
  return StyleSheet.create({
    // A wheel tick: FIXED width. <MeterRow> defends 44 in height because on a
    // row of neighbours you miss horizontally and land on an adjacent effort,
    // correctable with another tap. On a row that SCROLLS that reasoning
    // inverts — a horizontal miss is a drag — so the floor holds on both axes
    // and the ticks stop sharing the width. Height still comes from the
    // wheel's content container, so font scale grows them.
    tick: {
      width: EFFORT_TICK_WIDTH,
      alignItems: "center",
      justifyContent: "center",
    },
    // The collapsed (AMRAP) stop: one value, so it takes the row instead.
    wide: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
    },
    // PE-22 D2 — the prescribed-effort ghost, and the one place its shape is
    // argued. An INSET dashed ring: it frames the number the way the selection
    // ring does, because that is what reads as "aim here" — but it floats
    // inside the tick instead of hugging its edges.
    //
    // ⛔ La prima versione era una riga tratteggiata SOTTO il numero, e sul suo
    // telefono leggeva come un difetto di rendering, non come un bersaglio
    // (26/08, screenshot alla mano): «sembra un difetto, non che quello è il
    // numero da mirare». Una sottolineatura non dice "mira qui" — un contorno
    // sì, ed è quello che il mockup di AP-1 prometteva.
    //
    // L'inset è ciò che tiene in piedi la distinzione quando il tratteggio non
    // si vede: RN rende borderStyle:"dashed" come linea PIENA su certe
    // superfici Android, e un anello tratteggiato a filo diventerebbe allora
    // identico alla selezione — l'unica cosa che AP-1 vieta. Un rettangolo
    // staccato dai bordi resta un'altra forma anche pieno e anche sfocato.
    // Non tocca l'altezza della riga: il pavimento 44pt vive in <MeterRow>.
    targetMark: {
      position: "absolute",
      top: 4,
      left: 4,
      right: 4,
      bottom: 4,
      borderWidth: 2,
      borderStyle: "dashed",
      borderRadius: r.r2,
    },
    segmentNum: {
      fontSize: t.sizes.sm,
      fontWeight: "700",
      fontFamily: t.fonts.mono,
    },
    // PR pulse overlay — BORDER-ONLY in the status gold. A prFill wash was
    // tried first and killed the segment number (stop.on was validated against
    // the ramp fill, not prFill — 1.06:1 on the hard stops, a11y audit AP-0b),
    // so the number's colour never changes; the durable PR signal stays the
    // row's trophy tag.
    prFlash: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderWidth: 3,
      borderColor: theme.palette.pr,
    },
    pressed: { opacity: 0.7 },
    disabled: { opacity: 0.5 },
  });
}
